import { SimulationContext } from "../simulation/simulation_context.js";
import { CannonConfigService } from "../cannon_config/cannon_config_service.js";
import { PhysicsMass } from "../physics/physics_masses.js";

const STORAGE_KEY = "debug_ball_tuner";

type TunerState = { speed: number; mass: number };

export class BallTuner
{
  public static get BASE_SPEED(): number
  {
    return CannonConfigService.get().ball_speed;
  }

  public static readonly SPEED_MIN = 0;
  public static readonly SPEED_MAX = 200;
  public static readonly MASS_MIN = 0;
  public static readonly MASS_MAX = 30;

  private static readonly SPEED_HARD_MIN = 0.1;
  private static readonly SPEED_HARD_MAX = 100000;
  private static readonly MASS_HARD_MIN = 0.01;
  private static readonly MASS_HARD_MAX = 100000;

  private static speed_multiplier = 1;
  private static mass_multiplier = 1;
  private static loaded = false;

  private static panel: HTMLDivElement | null = null;
  private static speed_input: HTMLInputElement | null = null;
  private static mass_input: HTMLInputElement | null = null;
  private static speed_value: HTMLInputElement | null = null;
  private static mass_value: HTMLInputElement | null = null;
  private static speed_ratio: HTMLSpanElement | null = null;
  private static mass_ratio: HTMLSpanElement | null = null;

  public static applySpeed( base: number ): number
  {
    this.load();
    if ( SimulationContext.enabled )
      return base;
    return base * this.speed_multiplier;
  }

  public static applyMass( base: number ): number
  {
    this.load();
    if ( SimulationContext.enabled )
      return base;
    return base * this.mass_multiplier;
  }

  public static getSpeed(): number
  {
    this.load();
    return CannonConfigService.get().ball_speed * this.speed_multiplier;
  }

  public static getMass(): number
  {
    this.load();
    return CannonConfigService.get().ball_mass * this.mass_multiplier;
  }

  public static isModified(): boolean
  {
    this.load();
    return this.speed_multiplier !== 1 || this.mass_multiplier !== 1;
  }

  public static stateLabel(): string
  {
    return `${this.getSpeed().toFixed( 0 )} / ${this.getMass().toFixed( 2 )}`;
  }

  public static setSpeed( speed: number ): void
  {
    const clamped = this.clamp( speed, this.SPEED_HARD_MIN, this.SPEED_HARD_MAX );
    this.speed_multiplier = clamped / CannonConfigService.get().ball_speed;
    this.save();
    this.syncControls();
  }

  public static setMass( mass: number ): void
  {
    const clamped = this.clamp( mass, this.MASS_HARD_MIN, this.MASS_HARD_MAX );
    this.mass_multiplier = clamped / CannonConfigService.get().ball_mass;
    this.save();
    this.syncControls();
  }

  public static reset(): void
  {
    this.speed_multiplier = 1;
    this.mass_multiplier = 1;
    this.save();
    this.syncControls();
  }

  public static open(): void
  {
    this.load();
    const panel = this.buildPanel();
    panel.style.display = "block";
    this.syncControls();
  }

  public static close(): void
  {
    if ( this.panel )
      this.panel.style.display = "none";
  }

  public static toggle(): void
  {
    if ( this.panel && this.panel.style.display !== "none" )
      this.close();
    else
      this.open();
  }

  public static isOpen(): boolean
  {
    return this.panel !== null && this.panel.style.display !== "none";
  }

  private static clamp( value: number, min: number, max: number ): number
  {
    if ( !Number.isFinite( value ) )
      return min;
    return Math.min( max, Math.max( min, value ) );
  }

  private static load(): void
  {
    if ( this.loaded )
      return;
    this.loaded = true;

    try
    {
      const raw = window.localStorage.getItem( STORAGE_KEY );
      if ( !raw )
        return;

      const state = JSON.parse( raw ) as Partial<TunerState>;
      if ( Number.isFinite( state.speed ) )
        this.speed_multiplier = this.clamp( state.speed as number, this.SPEED_HARD_MIN, this.SPEED_HARD_MAX ) / CannonConfigService.get().ball_speed;
      if ( Number.isFinite( state.mass ) )
        this.mass_multiplier = this.clamp( state.mass as number, this.MASS_HARD_MIN, this.MASS_HARD_MAX ) / CannonConfigService.get().ball_mass;
    }
    catch ( error )
    {
      console.warn( "[BallTuner] не удалось прочитать сохранённые значения:", error );
    }
  }

  private static save(): void
  {
    try
    {
      const state: TunerState = { speed: this.getSpeed(), mass: this.getMass() };
      window.localStorage.setItem( STORAGE_KEY, JSON.stringify( state ) );
    }
    catch ( error )
    {
      console.warn( "[BallTuner] не удалось сохранить значения:", error );
    }
  }

  private static syncControls(): void
  {
    if ( this.speed_input )
      this.speed_input.value = String( this.clamp( this.getSpeed(), this.SPEED_MIN, this.SPEED_MAX ) );
    if ( this.mass_input )
      this.mass_input.value = String( this.clamp( this.getMass(), this.MASS_MIN, this.MASS_MAX ) );
    if ( this.speed_value && this.speed_value !== document.activeElement )
      this.speed_value.value = String( Number( this.getSpeed().toFixed( 2 ) ) );
    if ( this.mass_value && this.mass_value !== document.activeElement )
      this.mass_value.value = String( Number( this.getMass().toFixed( 2 ) ) );
    if ( this.speed_ratio )
      this.speed_ratio.textContent = `×${this.speed_multiplier.toFixed( 2 )}`;
    if ( this.mass_ratio )
      this.mass_ratio.textContent = `×${this.mass_multiplier.toFixed( 2 )}`;
  }

  private static buildPanel(): HTMLDivElement
  {
    if ( this.panel )
      return this.panel;

    const panel = document.createElement( "div" );
    panel.id = "debugBallTuner";
    panel.style.position = "fixed";
    panel.style.left = "12px";
    panel.style.top = "12px";
    panel.style.width = "260px";
    panel.style.zIndex = "2147483646";
    panel.style.background = "rgba(10, 12, 20, 0.92)";
    panel.style.color = "#fff";
    panel.style.font = "12px Arial, sans-serif";
    panel.style.borderRadius = "10px";
    panel.style.padding = "10px 12px 12px";
    panel.style.boxSizing = "border-box";
    panel.style.userSelect = "none";
    panel.style.display = "none";

    for ( const type of [ "pointerdown", "pointerup", "pointermove", "touchstart", "click" ] )
      panel.addEventListener( type, event => event.stopPropagation() );

    panel.appendChild( this.buildHeader( panel ) );

    const speed_row = this.buildSliderRow(
      "СИЛА (скорость вылета)",
      this.SPEED_MIN,
      this.SPEED_MAX,
      1,
      value => this.setSpeed( value )
    );
    this.speed_input = speed_row.input;
    this.speed_value = speed_row.value;
    this.speed_ratio = speed_row.ratio;
    panel.appendChild( speed_row.wrap );

    const mass_row = this.buildSliderRow(
      "МАССА ЯДРА",
      this.MASS_MIN,
      this.MASS_MAX,
      0.1,
      value => this.setMass( value )
    );
    this.mass_input = mass_row.input;
    this.mass_value = mass_row.value;
    this.mass_ratio = mass_row.ratio;
    panel.appendChild( mass_row.wrap );

    const hint = document.createElement( "div" );
    hint.textContent =
      `базовые: сила ${this.BASE_SPEED}, масса ${PhysicsMass.cannon_ball}\n` +
      `слайдер ${this.SPEED_MIN}-${this.SPEED_MAX} / ${this.MASS_MIN}-${this.MASS_MAX}, ` +
      `в поле можно вписать любое число\n` +
      `минимум по факту ${this.SPEED_HARD_MIN} и ${this.MASS_HARD_MIN}: ноль ломает физику`;
    hint.style.whiteSpace = "pre-line";
    hint.style.opacity = "0.55";
    hint.style.marginTop = "8px";
    panel.appendChild( hint );

    document.body.appendChild( panel );
    this.panel = panel;
    return panel;
  }

  private static buildHeader( panel: HTMLDivElement ): HTMLDivElement
  {
    const header = document.createElement( "div" );
    header.style.display = "flex";
    header.style.justifyContent = "space-between";
    header.style.alignItems = "center";
    header.style.marginBottom = "10px";
    header.style.cursor = "move";

    const title = document.createElement( "div" );
    title.textContent = "ЯДРО";
    title.style.font = "700 13px Arial, sans-serif";
    title.style.letterSpacing = "1px";

    const buttons = document.createElement( "div" );
    buttons.style.display = "flex";
    buttons.style.gap = "6px";

    const reset_btn = document.createElement( "button" );
    reset_btn.type = "button";
    reset_btn.textContent = "СБРОС";
    this.styleSmallButton( reset_btn, "#385090" );
    reset_btn.addEventListener( "click", () => this.reset() );

    const close_btn = document.createElement( "button" );
    close_btn.type = "button";
    close_btn.textContent = "✕";
    this.styleSmallButton( close_btn, "#c62828" );
    close_btn.addEventListener( "click", () => this.close() );

    buttons.appendChild( reset_btn );
    buttons.appendChild( close_btn );
    header.appendChild( title );
    header.appendChild( buttons );

    this.attachDrag( header, panel );
    return header;
  }

  private static attachDrag( handle: HTMLElement, panel: HTMLDivElement ): void
  {
    let dragging = false;
    let offset_x = 0;
    let offset_y = 0;

    handle.addEventListener( "pointerdown", event =>
    {
      if ( ( event.target as HTMLElement ).tagName === "BUTTON" )
        return;
      dragging = true;
      offset_x = event.clientX - panel.offsetLeft;
      offset_y = event.clientY - panel.offsetTop;
      handle.setPointerCapture( event.pointerId );
    } );

    handle.addEventListener( "pointermove", event =>
    {
      if ( !dragging )
        return;
      panel.style.left = `${Math.max( 0, event.clientX - offset_x )}px`;
      panel.style.top = `${Math.max( 0, event.clientY - offset_y )}px`;
    } );

    const stop = ( event: PointerEvent ): void =>
    {
      if ( !dragging )
        return;
      dragging = false;
      handle.releasePointerCapture( event.pointerId );
    };

    handle.addEventListener( "pointerup", stop );
    handle.addEventListener( "pointercancel", stop );
  }

  private static buildSliderRow(
    label_text: string,
    min: number,
    max: number,
    step: number,
    on_change: ( value: number ) => void
  ): { wrap: HTMLDivElement; input: HTMLInputElement; value: HTMLInputElement; ratio: HTMLSpanElement }
  {
    const wrap = document.createElement( "div" );
    wrap.style.marginBottom = "10px";

    const label = document.createElement( "div" );
    label.style.display = "flex";
    label.style.justifyContent = "space-between";
    label.style.alignItems = "center";
    label.style.gap = "6px";
    label.style.marginBottom = "4px";

    const name = document.createElement( "span" );
    name.textContent = label_text;
    name.style.opacity = "0.7";

    const readout = document.createElement( "div" );
    readout.style.display = "flex";
    readout.style.alignItems = "center";
    readout.style.gap = "6px";

    const value = document.createElement( "input" );
    value.type = "number";
    value.step = String( step );
    value.style.width = "62px";
    value.style.padding = "3px 4px";
    value.style.borderRadius = "4px";
    value.style.border = "1px solid rgba(255,255,255,0.25)";
    value.style.background = "rgba(255,255,255,0.08)";
    value.style.color = "#fff";
    value.style.font = "700 12px Arial, sans-serif";
    value.style.textAlign = "right";

    const ratio = document.createElement( "span" );
    ratio.style.opacity = "0.55";
    ratio.style.minWidth = "44px";

    readout.appendChild( value );
    readout.appendChild( ratio );
    label.appendChild( name );
    label.appendChild( readout );

    const input = document.createElement( "input" );
    input.type = "range";
    input.min = String( min );
    input.max = String( max );
    input.step = String( step );
    input.style.width = "100%";
    input.addEventListener( "input", () => on_change( Number( input.value ) ) );

    const commit = (): void =>
    {
      const typed = Number( value.value );
      if ( Number.isFinite( typed ) )
        on_change( typed );
      this.syncControls();
    };

    value.addEventListener( "change", commit );
    value.addEventListener( "keydown", event =>
    {
      if ( ( event as KeyboardEvent ).key === "Enter" )
        commit();
    } );
    value.addEventListener( "blur", () => this.syncControls() );

    wrap.appendChild( label );
    wrap.appendChild( input );

    return { wrap, input, value, ratio };
  }

  private static styleSmallButton( btn: HTMLButtonElement, background: string ): void
  {
    btn.style.padding = "6px 10px";
    btn.style.borderRadius = "6px";
    btn.style.border = "none";
    btn.style.background = background;
    btn.style.color = "#fff";
    btn.style.font = "700 11px Arial, sans-serif";
    btn.style.cursor = "pointer";
  }
}
